<template>
    <dialog-window
        v-model="isOpen"
        max-width="450"
        :title="t('travelCard.travelCardBlockTitle')"
        data-id="travelCardBlockDialog"
        @close="handleCancel"
    >
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section class-name="mb-2 px-3" :is-loading="false">
                <span class="text-body-1 pa-0 mb-2 primary-text-color d-flex align-center d-block">{{ travelCardBlockLabel }}</span>
                <form-description :text="t('travelCard.travelCardBlockDescription')" />
                <radio-button-group
                    id="card-action-select"
                    v-model="formData.selectedAction"
                    :required="true"
                    :options="travelCardBlockActions"
                    data-id="travelCardBlockFormActionSelect"
                />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        class="flex-1-0-0"
                        size="large"
                        :inactive-label="t('form.cancel')"
                        data-id="travelCardBlockFormCancelButton"
                        @click="handleCancel"
                    >
                        {{ t('form.cancel') }}
                    </card-button>
                    <card-button
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        color="error"
                        :inactive-label="t('form.blockCard')"
                        :loading="isSubmitting"
                        data-id="travelCardBlockFormSubmitButton"
                    >
                        {{ t('form.blockCard') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
    </dialog-window>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IFormProps } from '@/features/TravelCards/types';
import { blockTravelCard } from '../../../services/api';
import CardButton from '@/features/common/components/CardButton.vue';
import { useFormHandling, useNotification, useFormData } from '@/features/common/components/form/composables';
import { useMainStore } from '@/features/common/stores';
import { TRAVEL_CARD_BLOCK_ACTIONS_VALUES } from '../../../constants';
import { RadioButtonGroup, NotificationBar, FormDescription, FormSection } from '@/features/common/components/form';
import { generateUrl } from '@/helpers';
import { useAppConfiguration } from '@/composables';
import DialogWindow from '@/features/common/components/DialogWindow.vue';
import { useGlobalDialogStore } from '@/store/dialogStore';

const props = defineProps<IFormProps & { type: string }>();
const emit = defineEmits(['submit-success', 'cancel', 'update:isBlocked']);

const { t } = useI18n();

const initialFormState: any = {
    selectedAction: TRAVEL_CARD_BLOCK_ACTIONS_VALUES.BLOCK_ONLY
};

const mainStore = useMainStore();
const { config, language } = useAppConfiguration();
const { form, isSubmitting, toggleIsSubmitting, validateForm } = useFormHandling();
const { notificationBarState, notificationSuccess, notificationError } = useNotification();
const { isDialogOpen, closeDialog } = useGlobalDialogStore();
const { formData } = useFormData(initialFormState);

const isOpen = computed(() => isDialogOpen(props.type));

const travelCardBlockLabel = computed(() => `${t('travelCard.travelCardBlockConfirmation')} "${props.travelCardName}".`);

const travelCardBlockActions = computed(() => [
    {
        label: t('travelCard.travelCardBlock'),
        value: TRAVEL_CARD_BLOCK_ACTIONS_VALUES.BLOCK_ONLY
    },
    {
        label: t('travelCard.travelCardBlockAndRenew'),
        value: TRAVEL_CARD_BLOCK_ACTIONS_VALUES.BLOCK_AND_ORDER
    }
]);

const saveData = async () => {
    try {
        await blockTravelCard(props.travelCardId!);
        notificationBarState.value = notificationSuccess(t('travelCard.travelCardBlockedSuccess'));
        return true;
    } catch (error: any) {
        notificationBarState.value = notificationError(t('travelCard.travelCardBlockedFailed'));
        mainStore.registerError(error);
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        if (!props.travelCardId) return;
        const result = await saveData();
        if (result) {
            emit('update:isBlocked', true);
            if (formData.value.selectedAction === TRAVEL_CARD_BLOCK_ACTIONS_VALUES.BLOCK_AND_ORDER) {
                window.location.href = generateUrl(config.value.webshopBaseUrl, language.value, 'order-travelcard');
            }
            closeDialog(props.type);
        }
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};

const handleCancel = () => {
    closeDialog(props.type);
    emit('cancel');
};
</script>
